/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import * as angular from 'angular';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { Plan, PlanAccessLevel } from '@touchplan/common';
import { BehaviorSubject, Subject, exhaustMap, filter, takeUntil } from 'rxjs';

/**
 * @namespace Directives
 */
angular.module('ticketspaceApp').directive('projectView', ProjectViewDirective);

/**
 * @namespace projectViewDirective
 * @desc Main projectView directive
 * @memberOf Directives
 * @constructor
 */
ProjectViewDirective.$inject = [
	'ProjectViewCore',
	'settingStorage',
	'$window',
	'activeColumnService',
	'ProjectViewApi',
	'userPreferenceStateService',
];
function ProjectViewDirective(
	ProjectViewCore,
	settingStorage,
	$window,
	activeColumnService,
	ProjectViewApi,
	userPreferenceStateService
) {
	return {
		scope: true,
		controllerAs: 'ctrl',
		template: require('html-loader?root=src/!tpl/components/project-view-window.html'),
		bindToController: {
			pvBound: '=',
			pvWidth: '=',
			pvHeight: '=',
			timeline: '=',
			pullColumns: '=',
			milestone: '=',
			activeLine: '=',
			today: '=',
			hud: '=',
			plans: '=',
			plan: '=',
		},
		link(scope: any, element) {
			scope.element = element;
		},
		controller: [
			'$scope',
			function ($scope) {
				const logger = LoggerServiceFactory.create('ProjectViewDirective');
				let self = this;
				this.sizeSelected = settingStorage.setIfExists('projectView', 'sizeSelected', 'project-view-small');
				this.expanded = settingStorage.setIfExists('projectView', 'expanded', false);
				this.selectedHeatmap = settingStorage.setIfExists('projectView', 'selectedHeatmap', null);
				this.drawWaveformData = settingStorage.setIfExists('projectView', 'drawWaveformData', true);
				this.drawGuidelines = settingStorage.setIfExists('projectView', 'drawGuidelines', true);
				this.drawActiveLines = settingStorage.setIfExists('projectView', 'drawActiveLines', true);
				this.drawAheadText = settingStorage.setIfExists('projectView', 'drawAheadText', false);
				this.drawVerticalGuides = settingStorage.setIfExists('projectView', 'drawVerticalGuides', true);
				this.drawTimeline = settingStorage.setIfExists('projectView', 'drawTimeline', true);

				const onDestroy$ = new Subject<void>();
				const onAllLoaded$ = new Subject<boolean>();

				this.expand = function () {
					this.expanded = !this.expanded;
					if (this.expanded) {
						analyticsEventsService.projectViewSettingsClicked();
					}
				};

				this.resize = function (newSizeString, centerGuidelines) {
					if (!self.projectView) {
						return;
					}
					this.sizeSelected = newSizeString;
					let div = $('<div>').addClass(newSizeString).hide();
					$('body').append(div);
					let newWidth = div.width();
					let newHeight = div.height();
					div.remove();
					let element = document.getElementById('project-view-minimap');
					if (element) {
						if (!newWidth) {
							newWidth = 350;
						}
						if (!newHeight) {
							newHeight = 250;
						}
						element.style.width = newWidth + 'px';
						element.style.height = newHeight + 'px';
						self.projectView.resize(newWidth, newHeight, centerGuidelines);
					}
				};

				this.close = function () {
					$scope.toggleProjectView();
				};

				this.goToPlan = function () {
					settingStorage.save('projectView', {
						sizeSelected: this.sizeSelected,
						expanded: this.expanded,
						selectedHeatmap: this.selectedHeatmap,
						drawWaveformData: this.drawWaveformData,
						drawGuidelines: this.drawGuidelines,
						drawAheadText: this.drawAheadText,
						drawActiveLines: this.drawActiveLines,
						drawTimeline: this.drawTimeline,
						drawVerticalGuides: this.drawVerticalGuides,
						viewport: {
							pos: self.projectView.viewport.getPositions(),
							z: self.projectView.viewport.getScale(),
						},
						hud: {
							x: $scope.hud.screenToRealX(0),
							y: $scope.hud.screenToRealY(0),
							z: $scope.hud.z,
							transform: $scope.hud.transformation,
						},
						firstDate: this.projectView.getFirstVisibleDate(),
						autoPositionHud: true,
						autoOpen: true,
					});
					$scope.goToPlanProjectView(this.selectedHeatmap.$id);
				};

				this.setSelectedPlanBar = function (bar) {
					$scope.$apply(function () {
						self.selectedHeatmap = bar;
					});
				};

				this.setWaveform = function (value) {
					this.drawWaveformData = value;
					if (this.projectView) {
						this.projectView.setHeatmapVisibility(value);
					}
				};

				this.seeAll = function () {
					if (self.projectView) {
						self.projectView.seeAll();
						analyticsEventsService.projectViewSeeAllClicked();
					}
				};

				this.focusOnPlan = function (planId) {
					if (this.projectView) {
						this.projectView.focusOnPlan(planId, 0, 0.1, 2.5);
					}
				};

				this.setDrawAheadText = function (value) {
					this.drawAheadText = value;
					if (this.projectView) {
						this.projectView.setDrawAheadVisibility(value);
					}
				};

				this.setDrawGuidelines = function (value) {
					this.drawGuidelines = value;
					if (this.projectView) {
						this.projectView.setGuidelineVisibility(value);
					}
				};

				this.setTimelineVisibility = function (value) {
					this.drawTimeline = value;
					if (this.projectView) {
						this.projectView.setTimelineVisibility(value);
					}
				};

				this.setVerticalGuideVisibility = function (value) {
					this.drawVerticalGuides = value;
					if (this.projectView) {
						this.projectView.setVerticalGuideVisibility(value);
					}
				};

				this.setDrawActiveLines = function (value) {
					this.drawActiveLines = value;
					if (this.projectView) {
						this.projectView.setActiveLineVisibility(value);
					}
				};

				// Perform an AABB collision check against the X-axis of all tickets and the position of the plan view camera
				function normalizeCoordinates(startDate, z, topSpacing) {
					topSpacing = topSpacing || 500;
					let lowestTop = 99999999;
					let cameraScale = z;
					let newCameraLeftX = activeColumnService.guessXFromDate(startDate);
					let transformX = -newCameraLeftX * cameraScale;
					let newCameraRightX = $scope.hud.w / cameraScale - transformX / cameraScale;
					$scope.tickets.forEach(function (ticket) {
						if (ticket.data.left >= newCameraLeftX && ticket.data.left <= newCameraRightX) {
							if (ticket.data.top <= lowestTop) {
								lowestTop = ticket.data.top;
							}
						}
					});
					return {
						x: newCameraLeftX,
						y: lowestTop - topSpacing / cameraScale,
					};
				}

				// Load up the projectView once everything is loaded.
				$scope.$watch('_allLoaded', function (allLoaded: boolean) {
					if (!!allLoaded) {
						onAllLoaded$.next(true);
					}
				});

				onAllLoaded$
					.pipe(
						filter((allLoaded) => !!allLoaded),
						exhaustMap(() => self.plans as BehaviorSubject<Map<string, Plan>>),
						takeUntil(onDestroy$)
					)
					.subscribe((plans) => {
						if (self.projectView) {
							self.projectView.onPlanUpdate();
						} else if (!self.projectView && !$window.disableProjectView) {
							self.selectedHeatmap = null;
							// Used to control which features are enabled in the project overview.
							let features = {
								timeline: self.timeline,
								pullColumns: self.pullColumns,
								milestone: self.milestone,
								activeLine: self.activeLine,
								today: self.today,
								hud: self.hud,
								// It is necessary to retrieve the plan from the plans map because it contains the updated data structure,
								// which includes information on whether the plan is archived. The old plan object does not have this information.
								plan:
									plans.get(self.plan.$id)?.view.planAccess?.accessLevel === PlanAccessLevel.archived
										? null
										: self.plan,
							};
							let initOptions = {
								autoStart: false,
								width: self.pvWidth,
								height: self.pvHeight,
								resolution: 1,
								plans: plans,
							};
							self.projectView = new ProjectViewCore(
								document.getElementById(self.pvBound),
								initOptions,
								features
							);
							self.resize(self.sizeSelected);
							self.setWaveform(self.drawWaveformData);
							self.setDrawGuidelines(self.drawGuidelines);
							self.setDrawActiveLines(self.drawActiveLines);
							self.setDrawAheadText(self.drawAheadText);
							self.setTimelineVisibility(self.drawTimeline);
							self.setVerticalGuideVisibility(self.drawVerticalGuides);
							ProjectViewApi.build(self.projectView, self);

							// MOC-2693 MOC-3199
							// if ($scope.prefs.autoOpenProjectOverview || settingStorage.get('projectView').autoOpen){
							// 	// settingStorage.replace('projectView', 'autoOpen', false);
							// 	$scope.toggleSidebar('projectView');
							// 	//$scope.hud.scaleToFit();
							// 	logger.log("OPEN PROJECT VIEW");
							// }
							// $scope.hud.scaleToFit();
							// self.focusOnPlan();
							// MOC-3299
							if (
								$scope.userPreferenceStateService.autoOpenProjectOverview ||
								settingStorage.get('projectView').autoOpen
							) {
								$scope.toggleSidebar('projectView');
							}
							if (settingStorage.get('projectView').autoOpen) {
								settingStorage.replace('projectView', 'autoOpen', false);
								// $scope.toggleSidebar('projectView');

								// The planId must be specified in scenarios where the current plan is archived,
								// so the projectView is not provided with a plan in its initial features.
								self.focusOnPlan(self.plan.$id);

								// Collect the guesstimated normalized position, given the date
								let newPos = settingStorage.get('projectView').firstDate
									? normalizeCoordinates(
											settingStorage.get('projectView').firstDate,
											settingStorage.get('projectView').hud.z,
											300
									  )
									: {
											x: settingStorage.get('projectView').hud.x,
											y: settingStorage.get('projectView').hud.y,
									  };
								// Perform initial resize step of overview window..
								self.resize(self.sizeSelected);
								// Manually set the plan view camera position
								$scope.hud.setPosition(newPos.x, newPos.y, settingStorage.get('projectView').hud.z);
								// Manually set the Overview camera position
								self.projectView.viewport.setPosition(
									settingStorage.get('projectView').viewport.pos,
									settingStorage.get('projectView').viewport.z
								);
								settingStorage.replace('projectView', 'autoPositionHud', false);

								// logger.log("NORMALIZE COORDS!");
							} else {
								// $scope.toggleSidebar('projectView');
								$scope.hud.scaleToFit();

								// The planId must be specified in scenarios where the current plan is archived,
								// so the projectView is not provided with a plan in its initial features.
								self.focusOnPlan(self.plan.$id);

								// logger.log("Normal scale!");
							}
						}
					});

				let resizeTimer;
				// Resize the canvas when window changes size
				// Don't perform the resize operation every frame. Instead, debounce the call and do resize after some time has passed since the event was triggered.
				window.onresize = function (event) {
					clearTimeout(resizeTimer);
					resizeTimer = setTimeout(function () {
						self.resize(self.sizeSelected);
					}, 250);
				};
				$scope.$on('$destroy', () => {
					onDestroy$.next();
					onDestroy$.complete();
					onAllLoaded$.complete();
				});
			},
		],
	};
}
